/**
 * AST 기반 자동 생성된 검증 함수
 * 이 파일은 scripts/generate-validators.ts에 의해 자동 생성됩니다.
 * 수동으로 수정하지 마세요.
 * 
 * 생성 시간: 2026-09-18T08:13:01.392Z
 * 소스 파일: src/data/dto/admin/doctor-response-dto
 */

import type { DoctorDetailInfoResponseDto } from "@/data/dto/admin/doctor-response-dto";

/**
 * DoctorDetailInfoResponseDto 타입 가드 함수
 * AST 분석을 통해 자동 생성된 최적화된 검증 함수
 */
export function isDoctorDetailInfoResponseDto(input: unknown): input is DoctorDetailInfoResponseDto {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  
  const obj = input as Record<string, unknown>;
  
  return (
    typeof obj.name === "string" && obj.name.trim() !== "" &&
    typeof obj.screeningId === "string" &&
    typeof obj.email === "string" &&
    typeof obj.hospital === "string" &&
    (obj.memo === undefined || (obj.memo == null || typeof obj.memo === "string")) &&
    Array.isArray(obj.treatmentList) && obj.treatmentList.every((item) => typeof item === "object" && item !== null && !Array.isArray(item) &&
typeof (item as any).createdAt === "string" &&
typeof (item as any).patientId === "string" &&
typeof (item as any).treatmentUuid === "string" &&
typeof (item as any).patientUuid === "string" &&
((item as any).step == null || (typeof (item as any).step === "number" && !isNaN((item as any).step))) &&
  typeof (item as any).type === "number" && !isNaN((item as any).type)) &&
    typeof obj.isSnExisting === "boolean" &&
    typeof obj.isDeactivated === "boolean"
  );
}

/**
 * DoctorDetailInfoResponseDto 상세 검증 결과 반환 함수
 * 검증 실패 시 상세한 에러 정보를 반환합니다.
 */
export interface IValidation<T> {
  success: boolean;
  data?: T;
  errors?: Array<{ path: string; expected: string; value: unknown }>;
}

export function validateDoctorDetailInfoResponseDto(input: unknown): IValidation<DoctorDetailInfoResponseDto> {
  const errors: Array<{ path: string; expected: string; value: unknown }> = [];
  
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    errors.push({
      path: "",
      expected: "object",
      value: input,
    });
    return {
      success: false,
      errors,
    };
  }
  
  const obj = input as Record<string, unknown>;
  
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      errors.push({ path: "", expected: "object", value: obj });
    } else {
      const objObj = obj as Record<string, unknown>;
      // name (required)
      if (objObj.name === undefined) {
        errors.push({ path: "name", expected: "string", value: undefined });
      } else {
        if (!(typeof objObj.name === "string" && objObj.name.trim() !== "")) {
          errors.push({ path: "name", expected: "string (non-empty)", value: objObj.name });
        }
      }
      // screeningId (required)
      if (objObj.screeningId === undefined) {
        errors.push({ path: "screeningId", expected: "string", value: undefined });
      } else {
        if (typeof objObj.screeningId !== "string") {
          errors.push({ path: "screeningId", expected: "string", value: objObj.screeningId });
        }
      }
      // email (required)
      if (objObj.email === undefined) {
        errors.push({ path: "email", expected: "string", value: undefined });
      } else {
        if (typeof objObj.email !== "string") {
          errors.push({ path: "email", expected: "string", value: objObj.email });
        }
      }
      // hospital (required)
      if (objObj.hospital === undefined) {
        errors.push({ path: "hospital", expected: "string", value: undefined });
      } else {
        if (typeof objObj.hospital !== "string") {
          errors.push({ path: "hospital", expected: "string", value: objObj.hospital });
        }
      }
      // memo (optional)
      if (objObj.memo !== undefined) {
        if (!(objObj.memo == null || typeof objObj.memo === "string")) {
          errors.push({ path: "memo", expected: "string | null", value: objObj.memo });
        }
      }
      // treatmentList (required)
      if (objObj.treatmentList === undefined) {
        errors.push({ path: "treatmentList", expected: "array", value: undefined });
      } else {
        if (!Array.isArray(objObj.treatmentList)) {
          errors.push({ path: "treatmentList", expected: "array", value: objObj.treatmentList });
        } else {
          objObj.treatmentList.forEach((item, idx) => {
              if (typeof item !== "object" || item === null || Array.isArray(item)) {
          errors.push({ path: "treatmentList[idx]", expected: "object", value: item });
        } else {
          const itemObj = item as Record<string, unknown>;
          // createdAt (required)
          if (itemObj.createdAt === undefined) {
            errors.push({ path: "treatmentList[idx].createdAt", expected: "string", value: undefined });
          } else {
            if (typeof itemObj.createdAt !== "string") {
              errors.push({ path: "treatmentList[idx].createdAt", expected: "string", value: itemObj.createdAt });
            }
          }
          // patientId (required)
          if (itemObj.patientId === undefined) {
            errors.push({ path: "treatmentList[idx].patientId", expected: "string", value: undefined });
          } else {
            if (typeof itemObj.patientId !== "string") {
              errors.push({ path: "treatmentList[idx].patientId", expected: "string", value: itemObj.patientId });
            }
          }
          // treatmentUuid (required)
          if (itemObj.treatmentUuid === undefined) {
            errors.push({ path: "treatmentList[idx].treatmentUuid", expected: "string", value: undefined });
          } else {
            if (typeof itemObj.treatmentUuid !== "string") {
              errors.push({ path: "treatmentList[idx].treatmentUuid", expected: "string", value: itemObj.treatmentUuid });
            }
          }
          // patientUuid (required)
          if (itemObj.patientUuid === undefined) {
            errors.push({ path: "treatmentList[idx].patientUuid", expected: "string", value: undefined });
          } else {
            if (typeof itemObj.patientUuid !== "string") {
              errors.push({ path: "treatmentList[idx].patientUuid", expected: "string", value: itemObj.patientUuid });
            }
          }
          // step (required)
          if (itemObj.step === undefined) {
            errors.push({ path: "treatmentList[idx].step", expected: "number", value: undefined });
          } else {
            if (!(itemObj.step == null || (typeof itemObj.step === "number" && !isNaN(itemObj.step)))) {
              errors.push({ path: "treatmentList[idx].step", expected: "number | null", value: itemObj.step });
            }
          }
          // type (required)
          if (itemObj.type === undefined) {
            errors.push({ path: "treatmentList[idx].type", expected: "number", value: undefined });
          } else {
            if (!(typeof itemObj.type === "number" && !isNaN(itemObj.type))) {
              errors.push({ path: "treatmentList[idx].type", expected: "number", value: itemObj.type });
            }
          }
        }
          });
        }
      }
      // isSnExisting (required)
      if (objObj.isSnExisting === undefined) {
        errors.push({ path: "isSnExisting", expected: "boolean", value: undefined });
      } else {
        if (typeof objObj.isSnExisting !== "boolean") {
          errors.push({ path: "isSnExisting", expected: "boolean", value: objObj.isSnExisting });
        }
      }
      // isDeactivated (required)
      if (objObj.isDeactivated === undefined) {
        errors.push({ path: "isDeactivated", expected: "boolean", value: undefined });
      } else {
        if (typeof objObj.isDeactivated !== "boolean") {
          errors.push({ path: "isDeactivated", expected: "boolean", value: objObj.isDeactivated });
        }
      }
    }
  
  if (errors.length === 0 && isDoctorDetailInfoResponseDto(input)) {
    return {
      success: true,
      data: input,
    };
  }
  
  return {
    success: false,
    errors: errors.length > 0 ? errors : [
      {
        path: "",
        expected: "DoctorDetailInfoResponseDto",
        value: input,
      },
    ],
  };
}

/**
 * DoctorDetailInfoResponseDto 검증 후 에러 throw 함수
 * 검증 실패 시 상세한 에러 정보를 포함하여 에러를 throw합니다.
 */
export function assertDoctorDetailInfoResponseDto(input: unknown): DoctorDetailInfoResponseDto {
  const validation = validateDoctorDetailInfoResponseDto(input);
  if (!validation.success) {
    const errorDetails = validation.errors?.map(err => 
      `  - ${err.path}: 예상 타입 ${err.expected}, 실제 값: ${JSON.stringify(err.value)}`
    ).join("\n") || "알 수 없는 오류";
    throw new Error(`[assertDoctorDetailInfoResponseDto] :: 검증 실패: DoctorDetailInfoResponseDto 타입과 일치하지 않습니다\n\n오류 상세:\n${errorDetails}`);
  }
  return validation.data!;
}
